import { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  ListItem,
  Stack,
  Typography,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { t } from 'i18next';
import { Slice } from '@tiptap/pm/model';
import { BetaFlag } from 'components/generic/BetaFlag';
import BaseBubble from '../BaseBubble';
import { useEditorBubbleMenu } from '../../context';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import { CATEGORY_STYLES } from 'components/RichTextInput/StyleGuide/styleResolver';
import { parseOperationMeta } from 'components/RichTextInput/StyleGuide/types';

/**
 * Extracts display text from a ReplacementOption's content,
 * which can be either a plain string or a ProseMirror Slice.
 */
const getReplacementText = (content: string | Slice): string => {
  if (typeof content === 'string') {
    return content;
  }

  const fragment = content.content;
  return fragment.textBetween(0, fragment.size);
};

const CATEGORY_LABELS: Record<string, string> = {
  abbreviations: 'Abbreviations',
  capitalization: 'Capitalization',
  numbers: 'Numbers',
  'dates-times': 'Dates & Times',
  punctuation: 'Punctuation',
  'common-errors': 'Common Errors',
};

const AiSuggestBubble = () => {
  const theme = useTheme();
  const { editor, bubbleViewState, bubbleCleanup } = useEditorBubbleMenu();

  const aiToolkit = editor ? getAiToolkit(editor) : null;
  const selectedSuggestion = aiToolkit?.getSelectedSuggestion() ?? null;

  // The proofreader workflow stores per-operation metadata as a JSON string
  // in suggestion.metadata.operationMeta. Parse it to get category, ruleTitle, etc.
  const opMeta = parseOperationMeta(selectedSuggestion?.metadata?.operationMeta);

  const category = opMeta?.category;
  const categoryStyle = CATEGORY_STYLES[category ?? ''] ?? CATEGORY_STYLES.numbers;
  const categoryLabel = CATEGORY_LABELS[category ?? ''] ?? category;

  const handleApply = useCallback(
    (suggestionId: string, replacementOptionId: string) => {
      if (!aiToolkit || !suggestionId || !replacementOptionId) {
        return;
      }

      aiToolkit.acceptSuggestion(suggestionId, {
        replacementOptionId,
      });

      bubbleCleanup();
    },
    [aiToolkit, bubbleCleanup],
  );

  const handleReject = useCallback(
    (suggestionId: string) => {
      if (!aiToolkit || !suggestionId) {
        return;
      }

      aiToolkit.rejectSuggestion(suggestionId);

      bubbleCleanup();
    },
    [aiToolkit, bubbleCleanup],
  );

  const replacementItems = useMemo(() => {
    if (!selectedSuggestion) return [];
    return selectedSuggestion.replacementOptions.map((option) => ({
      id: option.id,
      text: getReplacementText(option.content),
    }));
  }, [selectedSuggestion]);

  if (!editor || bubbleViewState !== 'aiSuggestBubble' || !selectedSuggestion) {
    return null;
  }

  return (
    <BaseBubble
      sx={{
        width: 280,
      }}
      title={
        <Box
          data-testid="ai-suggest-bubble-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {t(opMeta?.ruleTitle ?? '')} <BetaFlag />
        </Box>
      }
      subheader={opMeta?.message}
    >
      <Box key={selectedSuggestion.id}>
        {/* Category indicator */}
        {categoryLabel && (
          <Chip
            label={categoryLabel}
            size="small"
            sx={{
              mb: 1,
              backgroundColor: categoryStyle.background,
              color: categoryStyle.underline,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderLeft: `3px solid ${categoryStyle.underline}`,
            }}
          />
        )}

        {/* Replacement Options */}
        <Stack spacing={1}>
          {replacementItems.map((item) => (
            <ListItem
              key={item.id}
              data-testid={`ai-suggest-bubble-option-${item.id}`}
              onClick={() => handleApply(selectedSuggestion.id, item.id)}
              sx={{
                justifyContent: 'flex-start',
                height: 'auto',
                py: 1,
                px: 1.5,
                borderLeft: `3px solid ${categoryStyle.underline}`,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: `${alpha(theme.palette.success.main, 0.1)} !important`,
                },
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Typography variant="body2">{item.text}</Typography>
            </ListItem>
          ))}
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
            mt: 1,
          }}
        >
          <Tooltip title={t('blox.general.ignore')}>
            <Button
              data-testid="ai-suggest-bubble-reject-button"
              size="small"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleReject(selectedSuggestion.id)}
            >
              {t('blox.general.ignore')}
            </Button>
          </Tooltip>
        </Stack>
      </Box>
    </BaseBubble>
  );
};

export default AiSuggestBubble;
